import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack, Edit, Cancel, RequestQuote, EmojiEvents, ThumbDown } from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { RouterLink } from '../../utils/linkNav';

// ---------------------------------------------------------------------------
// Customer Enquiry 360: header + lifecycle ribbon, line items, and the
// workflow actions that move an enquiry toward a quotation. Mirrors
// RfqDetailPage.tsx's overall shape.
// ---------------------------------------------------------------------------

type EnquiryStatus = 'OPEN' | 'QUOTED' | 'WON' | 'LOST';
type EnquiryPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'OPEN', label: 'Open' },
  { key: 'QUOTED', label: 'Quoted' },
  { key: 'WON', label: 'Won' },
];

const FLOW_INDEX: Partial<Record<EnquiryStatus, number>> = {
  OPEN: 0,
  QUOTED: 1,
  WON: 2,
};

const STATUS_TONE: Record<EnquiryStatus, StatusTone> = {
  OPEN: 'info',
  QUOTED: 'warning',
  WON: 'success',
  LOST: 'error',
};

const PRIORITY_TINT: Record<EnquiryPriority, string> = {
  LOW: '#64748b',
  MEDIUM: '#0284c7',
  HIGH: '#d97706',
  URGENT: '#dc2626',
};

interface EnquiryItem {
  id?: number;
  product_id: string | number | null;
  product_name?: string | null;
  description: string | null;
  quantity: number | string;
  uom: string | null;
  target_price: number | string | null;
  required_delivery_date: string | null;
}

interface EnquiryDetail {
  id: number;
  enquiry_number: string;
  customer_id: string | number;
  customer_name?: string;
  contact_person?: string | null;
  enquiry_date: string;
  valid_until: string | null;
  salesperson_name?: string | null;
  source: string;
  probability?: number | string | null;
  priority: EnquiryPriority;
  notes: string | null;
  status: EnquiryStatus;
  items: EnquiryItem[];
}

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const CustomerEnquiryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [enquiry, setEnquiry] = useState<EnquiryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { enquiry: data } = await api.get<{ enquiry: EnquiryDetail }>(`/customer-enquiries/${id}`);
      setEnquiry(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load enquiry');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !enquiry) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/enquiries')} sx={{ mb: 2 }}>
          Back to Enquiries
        </Button>
        <Alert severity="error">{error || 'Enquiry not found'}</Alert>
      </Box>
    );
  }

  const isSideState = (['WON', 'LOST'] as EnquiryStatus[]).includes(enquiry.status) && enquiry.status === 'LOST';
  const flowIndex = FLOW_INDEX[enquiry.status] ?? 0;

  const canEdit = enquiry.status === 'OPEN';
  const canMarkQuoted = enquiry.status === 'OPEN';
  const canConvert = enquiry.status === 'OPEN' || enquiry.status === 'QUOTED';
  const canMarkWon = enquiry.status === 'QUOTED';
  const canMarkLost = enquiry.status === 'OPEN' || enquiry.status === 'QUOTED';
  const noActionsAvailable = !canEdit && !canMarkQuoted && !canConvert && !canMarkWon && !canMarkLost;

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const setStatus = (next: EnquiryStatus, label: string) =>
    runAction(`status-${next}`, async () => {
      await api.put(`/customer-enquiries/${id}/status`, { status: next });
      setNotice(label);
      await load();
    });

  const handleConvert = () =>
    runAction('convert', async () => {
      const res: any = await api.post(`/customer-enquiries/${id}/convert-to-quotation`);
      const quotationNumber = res?.quotation?.quotationNumber || res?.quotation?.quotation_number;
      setNotice(quotationNumber ? `Converted to quotation ${quotationNumber}.` : 'Converted to a sales quotation.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/enquiries')} sx={{ mb: 1 }}>
        Back to Enquiries
      </Button>

      <PageHeader
        title={enquiry.enquiry_number}
        qualifier={enquiry.customer_name ? `· ${enquiry.customer_name}` : undefined}
        subtitle={`Raised ${fmtDate(enquiry.enquiry_date)}${enquiry.valid_until ? ` · Valid until ${fmtDate(enquiry.valid_until)}` : ''}`}
        actions={
          <>
            <TypePill label={enquiry.priority} tint={PRIORITY_TINT[enquiry.priority] || '#475569'} />
            <StatusDot label={enquiry.status} tone={STATUS_TONE[enquiry.status]} />
          </>
        }
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity="warning" icon={<ThumbDown />}>
              <Typography sx={{ fontWeight: 700 }}>Enquiry Lost</Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button variant="outlined" startIcon={<Edit />} component={RouterLink} to={`/sales/enquiries/${id}/edit`}>
              Edit
            </Button>
          )}
          {canMarkQuoted && (
            <Button
              variant="outlined"
              disabled={actionLoading !== null}
              onClick={() => setStatus('QUOTED', 'Enquiry marked as quoted.')}
            >
              {actionLoading === 'status-QUOTED' ? 'Updating...' : 'Mark as Quoted'}
            </Button>
          )}
          {canConvert && (
            <Button
              variant="contained"
              startIcon={<RequestQuote />}
              disabled={actionLoading !== null}
              onClick={handleConvert}
            >
              {actionLoading === 'convert' ? 'Converting...' : 'Convert to Quotation'}
            </Button>
          )}
          {canMarkWon && (
            <Button
              variant="contained"
              color="success"
              startIcon={<EmojiEvents />}
              disabled={actionLoading !== null}
              onClick={() => setStatus('WON', 'Enquiry marked as won.')}
            >
              {actionLoading === 'status-WON' ? 'Updating...' : 'Mark Won'}
            </Button>
          )}
          {canMarkLost && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              disabled={actionLoading !== null}
              onClick={() => setStatus('LOST', 'Enquiry marked as lost.')}
            >
              {actionLoading === 'status-LOST' ? 'Updating...' : 'Mark Lost'}
            </Button>
          )}
          {noActionsAvailable && (
            <Typography variant="body2" color="text.secondary">
              This enquiry is {enquiry.status.toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Enquiry Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Customer', enquiry.customer_name],
                ['Contact Person', enquiry.contact_person],
                ['Salesperson', enquiry.salesperson_name],
                ['Source', enquiry.source?.replace(/_/g, ' ')],
                ['Enquiry Date', fmtDate(enquiry.enquiry_date)],
                ['Valid Until', fmtDate(enquiry.valid_until)],
                ['Win Probability', enquiry.probability !== null && enquiry.probability !== undefined ? `${toNum(enquiry.probability)}%` : null],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Notes
            </Typography>
            <Typography variant="body2" color={enquiry.notes ? 'text.primary' : 'text.disabled'} sx={{ whiteSpace: 'pre-wrap' }}>
              {enquiry.notes || 'No notes recorded.'}
            </Typography>
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line Items
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Product', 'Qty', 'UOM', 'Target Price', 'Required By'].map((h, i) => (
                      <TableCell key={h} align={i === 1 || i === 3 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enquiry.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <TableEmptyState title="No line items on this enquiry" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    enquiry.items.map((item, idx) => (
                      <TableRow key={item.id ?? idx} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25 }}>{item.product_name || item.description || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.quantity)}
                        </TableCell>
                        <TableCell>{item.uom || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {item.target_price !== null && item.target_price !== undefined ? formatIndianNumber(toNum(item.target_price)) : '—'}
                        </TableCell>
                        <TableCell>{fmtDate(item.required_delivery_date)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AttachmentsPanel entityType="customer_enquiry" entityId={enquiry.id} title="Enquiry Documents" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerEnquiryDetailPage;
